import React, { useState } from 'react';
import { useToast } from '../../sectepe-ui/index';
import leadSyncService, { EnrichResult, Lead } from '../../services/leadSyncService';
import { EmptyState } from '../../components/ui';
import { Icon } from '../../components/icons';
import { fehlerText } from '../format';
import { ErgebnisTabelle } from './ErgebnisTabelle';

interface Props {
  leads: Lead[];
  ausgewaehlt: string[];
  onGeladen: () => Promise<void> | void;
  onFehler: (text: string | null) => void;
}

/** Clay: ausgewaehlte Leads direkt anreichern oder ueber eine Clay-Tabelle. */
export const AnreicherungPanel: React.FC<Props> = ({ leads, ausgewaehlt, onGeladen, onFehler }) => {
  const { push } = useToast();
  const [tabelle, setTabelle] = useState('');
  const [laeuft, setLaeuft] = useState<null | 'direkt' | 'push' | 'pull'>(null);
  const [ergebnis, setErgebnis] = useState<EnrichResult | null>(null);
  const angereichert = leads.filter((l) => l.clayEnrichedAt).length;

  const lauf = async (art: 'direkt' | 'push' | 'pull', fn: () => Promise<EnrichResult | null>, meldung: (r: EnrichResult | null) => string) => {
    setLaeuft(art);
    onFehler(null);
    try {
      const r = await fn();
      if (r) setErgebnis(r);
      push({ message: meldung(r), severity: 'success' });
      await onGeladen();
    } catch (e) {
      onFehler(fehlerText(e, 'Clay-Aufruf fehlgeschlagen'));
    } finally {
      setLaeuft(null);
    }
  };

  if (leads.length === 0) {
    return <EmptyState compact icon="sparkles" title="Keine Leads zum Anreichern" description="Erst Leads importieren oder anlegen, dann hier mit Clay Positionen, Telefonnummern und LinkedIn-Profile ergänzen." />;
  }

  return (
    <div className="lead-panel">
      <div className="grid-2-col">
        <section className="card" aria-label="Direkt anreichern">
          <h3 className="card-title">Direkt anreichern</h3>
          <p className="card-description">Clay ergänzt zu den ausgewählten Leads Position, Telefon und LinkedIn-Profil. {angereichert} von {leads.length} Leads sind schon angereichert.</p>
          <button type="button" className="btn btn-primary" onClick={() => lauf('direkt', () => leadSyncService.enrichLeads(ausgewaehlt), (r) => `${r?.enriched ?? 0} Leads angereichert.`)} disabled={laeuft !== null || ausgewaehlt.length === 0}>
            <Icon name="sparkles" size={14} /> {laeuft === 'direkt' ? 'Reichert an…' : `${ausgewaehlt.length} ausgewählte anreichern`}
          </button>
          {ausgewaehlt.length === 0 && <p className="text-xs text-secondary">Oben in der Liste Leads auswählen.</p>}
        </section>

        <section className="card" aria-label="Clay-Tabelle">
          <h3 className="card-title">Über eine Clay-Tabelle</h3>
          <p className="card-description">Leads in eine Clay-Tabelle schicken, dort anreichern lassen und die Ergebnisse zurückholen.</p>
          <label className="lead-panel__feld">
            Tabellen-ID
            <input type="text" placeholder="tbl_…" value={tabelle} onChange={(e) => setTabelle(e.target.value)} />
          </label>
          <div className="btn-group">
            <button type="button" className="btn btn-secondary" onClick={() => lauf('push', async () => { await leadSyncService.pushToClayTable(ausgewaehlt, tabelle.trim()); return null; }, () => `${ausgewaehlt.length} Leads an die Clay-Tabelle geschickt.`)} disabled={laeuft !== null || ausgewaehlt.length === 0 || !tabelle.trim()}>
              {laeuft === 'push' ? 'Schickt…' : `Hinschicken (${ausgewaehlt.length})`}
            </button>
            <button type="button" className="btn btn-secondary" onClick={() => lauf('pull', () => leadSyncService.pullFromClayTable(tabelle.trim()), (r) => `${r?.enriched ?? 0} Leads aus der Clay-Tabelle aktualisiert.`)} disabled={laeuft !== null || !tabelle.trim()}>
              {laeuft === 'pull' ? 'Holt…' : 'Ergebnisse holen'}
            </button>
          </div>
        </section>
      </div>

      {ergebnis && <ErgebnisTabelle titel="Ergebnis der Anreicherung" gut={ergebnis.enriched} gutLabel="angereichert" schlecht={ergebnis.failed} details={ergebnis.details} />}
    </div>
  );
};
